import type {ChangeEvent, FocusEvent, KeyboardEvent, ReactElement} from 'react';
import type {Editor} from 'grapesjs';
import {useCallback, useContext, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerMode} from '@EmailMakerReact/email-maker-react.types';
import {NEW_TEMPLATE_NAME, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getTemplateName} from '@EmailMakerReact/lib/email-maker-save-payload-library';
import {useSyncTemplateNameFromWrapper} from './template-name-input.hooks';
import {TEMPLATE_NAME_MAX_LENGTH} from './template-name-input.types';
import styles from './template-name-input.module.scss';

/**
 * Reads the name from the live GrapesJS wrapper prop — the single source of truth that save reads
 * and load seeds — never the immutable boot store, which goes stale the moment the user renames.
 * Renaming only sets the name on the wrapper; the next autosave/manual save persists it.
 */
export function TemplateNameInput(): ReactElement {
  const {store, editor} = useContext(emailMakerContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialName = editor ? getTemplateName(editor) : NEW_TEMPLATE_NAME;
  const previousNameRef = useRef<string>(initialName);
  const [inputSize, setInputSize] = useState<number>(() => Math.max(initialName.length, 1));

  const isLocked = store.isApproved && store.mode === EmailMakerMode.LOAD;

  const applyExternalName = useCallback((name: string): void => {
    previousNameRef.current = name;
    setInputSize(Math.max(name.length, 1));
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = name;
    }
  }, []);

  useSyncTemplateNameFromWrapper(editor, applyExternalName);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setInputSize(Math.max(event.currentTarget.value.length, 1));
  }, []);

  const commitRename = useCallback(
    (rawValue: string): void => {
      const trimmed = rawValue.trim();
      if (!trimmed) {
        if (inputRef.current) {
          inputRef.current.value = previousNameRef.current;
        }
        return;
      }
      previousNameRef.current = trimmed;
      pushNameToEditor(editor, trimmed);
    },
    [editor]
  );

  const handleFocus = useCallback((): void => {
    inputRef.current?.select();
  }, []);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      commitRename(event.currentTarget.value);
    },
    [commitRename]
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.currentTarget.value = previousNameRef.current;
      setInputSize(Math.max(previousNameRef.current.length, 1));
      event.currentTarget.blur();
    }
  }, []);

  const handlePencilClick = useCallback((): void => {
    if (!isLocked) {
      inputRef.current?.focus();
    }
  }, [isLocked]);

  return (
    <div className={styles.wrapper}>
      <input
        aria-label={window.i18next.t('pmwjs_template_name')}
        className={styles.input}
        defaultValue={initialName}
        disabled={isLocked}
        maxLength={TEMPLATE_NAME_MAX_LENGTH}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        size={inputSize}
        type="text"
      />
      <Icon ariaHidden className={styles.editIcon} icon="icon-pencil" onClick={handlePencilClick} size={IconSize.SIZE_ICON_20} />
    </div>
  );
}

function pushNameToEditor(editor: Editor | null, name: string): void {
  if (!editor) {
    return;
  }
  const wrapper = getMainWrapper(editor);
  if (wrapper) {
    wrapper.set(EMAIL_MAKER_PROPS.TEMPLATE_NAME, name);
  }
}
